// =====================================================================
//  Bloc « Compte de service » de la fiche client (onglet Généralités)
//  ---------------------------------------------------------------------
//  Identité technique de l'application du client (header X-Account).
//  La clé (refresh token) est masquée ; « Révéler » l'affiche et
//  « Régénérer » en émet une nouvelle en révoquant l'ancienne. Ces deux
//  actions sont tracées au journal métier côté API.
// =====================================================================

import {IDisposableLike} from "../../Tools/Disposable/IDisposableLike";
import {IClientServiceAccountModel} from "../../Model/Client/ClientServiceAccountModel.js";
import {ClientServiceAccount} from "../../Controller/Client/ClientServiceAccount.js";
import {NotificationHelper} from "../../Helper/NotificationHelper.js";
import {Clipboard} from "../../Tools/Clipboard.js";

const MASK = "••••••••••••••••••••";

export class ServiceAccountPanel implements IDisposableLike {
    readonly #jqMainContainer: JQuery;
    readonly #clientId: string;
    #jqContainer: JQuery | undefined;
    #model: IClientServiceAccountModel | null = null;
    #revealed: string | null = null;

    constructor(options: IServiceAccountPanelOptions) {
        this.#jqMainContainer = options.jqMainContainer;
        this.#clientId = options.clientId;
    }

    public Init = async () => {
        this.#jqContainer = $("<fieldset></fieldset>")
            .addClass("k-form-fieldset")
            .css({border: "1px solid var(--kendo-color-border, #ddd)", borderRadius: "4px", padding: "8px 12px", maxWidth: "720px"})
            .appendTo(this.#jqMainContainer);
        await this.#load();
    };

    #load = async () => {
        this.#model = await ClientServiceAccount.Read(this.#clientId);
        this.#revealed = null;
        this.#render();
    };

    #render = () => {
        const c = this.#jqContainer.empty();
        $("<legend></legend>").addClass("k-form-legend").text("Compte de service").appendTo(c);
        $("<p></p>").css({margin: "0 0 8px 0", fontSize: "12px", opacity: 0.8})
            .text("Identité de l'application du client qui appelle EASYMAIL. La clé se dépose dans la configuration de l'application ; " +
                "elle ne doit jamais circuler par mail, ticket ou assistant IA.")
            .appendTo(c);

        const m = this.#model;
        if (!m) {
            $("<p></p>").css({margin: "0 0 8px 0", color: "#c62828", fontWeight: "bold"})
                .text("Aucun compte de service : l'application de ce client ne peut pas appeler EASYMAIL.")
                .appendTo(c);
            this.#button("Créer le compte de service", "primary", this.#create).appendTo(c);
            return;
        }

        const table = $("<table></table>").css({borderSpacing: "0 4px"}).appendTo(c);
        const row = (label: string, value: JQuery | string) => {
            const tr = $("<tr></tr>").appendTo(table);
            $("<td></td>").css({paddingRight: "16px", fontWeight: "bold", whiteSpace: "nowrap", verticalAlign: "top"}).text(label).appendTo(tr);
            const td = $("<td></td>").appendTo(tr);
            typeof value === "string" ? td.text(value) : td.append(value);
        };

        row("Login", this.#copyable(m.Login ?? "", "Login copié."));
        row("Libellé", m.Libelle ?? "");
        row("Créé le", ServiceAccountPanel.#date(m.CreateAt));

        if (!m.HasActiveToken) {
            row("Clé", $("<span></span>").css({color: "#c62828"}).text("Aucune clé active (expirée ou révoquée)."));
        } else {
            const key = $("<span></span>").css({display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap"});
            $("<code></code>").css({padding: "2px 6px", background: "rgba(0,0,0,0.05)", wordBreak: "break-all"})
                .text(this.#revealed ?? `${MASK} ${m.TokenHint ?? ""}`).appendTo(key);
            if (this.#revealed) {
                this.#button("Copier", "base", async () => this.#copy(this.#revealed!, "Clé copiée.")).appendTo(key);
                this.#button("Masquer", "base", async () => {
                    this.#revealed = null;
                    this.#render();
                }).appendTo(key);
            } else {
                this.#button("Révéler", "base", this.#reveal).appendTo(key);
            }
            row("Clé", key);
            row("Expire le", ServiceAccountPanel.#date(m.TokenExpiresAt));
            row("Dernière utilisation", m.TokenLastUsageAt ? ServiceAccountPanel.#date(m.TokenLastUsageAt) : "jamais");
        }

        const actions = $("<div></div>").css({marginTop: "8px"}).appendTo(c);
        this.#button(m.HasActiveToken ? "Régénérer la clé" : "Émettre une clé", m.HasActiveToken ? "error" : "primary", this.#regenerate)
            .appendTo(actions);
    };

    #create = async () => {
        if (await ClientServiceAccount.Create(this.#clientId)) {
            NotificationHelper.Instance.success("Compte de service créé.");
            await this.#load();
        }
    };

    #reveal = async () => {
        const res = await ClientServiceAccount.Reveal(this.#clientId);
        if (!res?.Token)
            return;
        this.#revealed = res.Token;
        this.#render();
    };

    #regenerate = async () => {
        if (this.#model?.HasActiveToken) {
            const ok = await kendo.confirm(
                "La clé actuelle sera <b>révoquée immédiatement</b> : l'application du client ne pourra plus appeler EASYMAIL " +
                "tant que la nouvelle clé n'aura pas été déposée dans sa configuration.<br/>Continuer ?")
                .then(() => true, () => false);
            if (!ok)
                return;
        }
        const res = await ClientServiceAccount.Regenerate(this.#clientId);
        if (!res?.Token)
            return;
        this.#model = {...res, Token: undefined};
        this.#revealed = res.Token;
        this.#render();
        NotificationHelper.Instance.warning("Nouvelle clé émise : déposez-la dans la configuration de l'application.");
    };

    #copyable = (text: string, message: string): JQuery => {
        const span = $("<span></span>").css({display: "flex", gap: "6px", alignItems: "center"});
        $("<code></code>").text(text).appendTo(span);
        this.#button("Copier", "base", async () => this.#copy(text, message)).appendTo(span);
        return span;
    };

    #copy = async (text: string, message: string) => {
        if (await Clipboard.Copy(text))
            NotificationHelper.Instance.success(message);
        else
            NotificationHelper.Instance.error("Copie impossible : sélectionnez le texte à la main.");
    };

    #button = (text: string, theme: "base" | "primary" | "error", action: () => Promise<void>): JQuery => {
        const b = $("<button type='button'></button>")
            .addClass(`k-button k-button-sm k-rounded-md k-button-solid k-button-solid-${theme}`)
            .text(text);
        b.on("click", async () => {
            b.prop("disabled", true);
            try {
                await action();
            } finally {
                b.prop("disabled", false);
            }
        });
        return b;
    };

    static #date = (d?: Date | string): string =>
        d ? kendo.toString(new Date(d), "dd/MM/yyyy HH:mm") : "—";

    public Dispose = () => {
        this.#revealed = null;
        this.#jqContainer?.remove();
    };
}

export interface IServiceAccountPanelOptions {
    jqMainContainer: JQuery;
    clientId: string;
}
